import React, { useState } from 'react';

import type { PageDimensions } from '@aline/common';
import { Divider, MenuItem } from '@mui/material';
import { equals, pick } from 'ramda';
import { IndentLeftIcon } from 'src/components/icons/IndentLeftIcon';
import { IndentRightIcon } from 'src/components/icons/IndentRightIcon';
import { LandscapeIcon } from 'src/components/icons/LandscapeIcon';
import { ModerateMarginsIcon } from 'src/components/icons/ModerateMarginsIcon';
import { NarrowMarginsIcon } from 'src/components/icons/NarrowMarginsIcon';
import { NormalMarginsIcon } from 'src/components/icons/NormalMarginsIcon';
import { PageMarginsIcon } from 'src/components/icons/PageMarginsIcon';
import { PageOrientationIcon } from 'src/components/icons/PageOrientationIcon';
import { PageSizeIcon } from 'src/components/icons/PageSizeIcon';
import { PortraitIcon } from 'src/components/icons/PortraitIcon';
import { SpacingAfterIcon } from 'src/components/icons/SpacingAfterIcon';
import { SpacingBeforeIcon } from 'src/components/icons/SpacingBeforeIcon';
import { WideMarginsIcon } from 'src/components/icons/WideMarginsIcon';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { ButtonMenu } from 'src/editor/toolbars/tabs/layout-tab/ButtonMenu';
import { ParagraphPropStepper } from 'src/editor/toolbars/tabs/layout-tab/ParagraphPropStepper';
import type { PageSize } from 'src/editor/toolbars/tabs/layout-tab/utils';
import { getPageSizeByName, pageSizes } from 'src/editor/toolbars/tabs/layout-tab/utils';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { ButtonsBarWithOverflowMenu } from '../shared/ButtonsBarWithOverflowMenu';

const useStyles = makeStyles<void, 'menuItemDescription'>()((theme, _params, classes) => ({
  buttonMenuDivider: {
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  divider: {
    height: 24,
    marginLeft: theme.spacing(0.8),
    marginRight: theme.spacing(0.8),
  },
  marginsIcon: {
    height: 39,
    width: 32,
  },
  menuItem: {
    '&:hover': {
      [`& .${classes.menuItemDescription}`]: {
        color: theme.appColors.primary,
      },
      color: theme.appColors.primary,
    },
    display: 'flex',
  },
  menuItemDescription: {
    color: theme.appColors.secondaryType,
    fontSize: 12,
  },
  menuItemHeader: {
    fontWeight: 'bold',
  },
  menuItemIcon: {
    display: 'block',
    marginRight: theme.spacing(1),
  },
  menuItemSelected: {
    fontWeight: 'bold',
  },
}));

export function LayoutButtons(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const {
    documentUi: { setPageSetupDialogOpen },
  } = useEditorTabStateActions();
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const indentationLeft = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.indentationLeft
  );
  const indentationRight = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.indentationRight
  );
  const spacingAfter = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.spacingAfter
  );
  const spacingBefore = useEditorTabStateSelector(
    state => state.derived.paragraphProperties.spacingBefore
  );

  const [pageMarginsOpen, setPageMarginsOpen] = useState<boolean>(false);
  const [pageOrientationOpen, setPageOrientationOpen] = useState<boolean>(false);
  const [pageSizeOpen, setPageSizeOpen] = useState<boolean>(false);

  const margins = [
    {
      icon: <NormalMarginsIcon classes={{ root: classes.menuItemIcon }} />,
      name: 'Normal',
      pageDimensions: {
        marginBottom: 1,
        marginLeft: 1,
        marginRight: 1,
        marginTop: 1,
      },
    },
    {
      icon: <NarrowMarginsIcon classes={{ root: classes.menuItemIcon }} />,
      name: 'Narrow',
      pageDimensions: {
        marginBottom: 0.5,
        marginLeft: 0.5,
        marginRight: 0.5,
        marginTop: 0.5,
      },
    },
    {
      icon: <ModerateMarginsIcon classes={{ root: classes.menuItemIcon }} />,
      name: 'Moderate',
      pageDimensions: {
        marginBottom: 1,
        marginLeft: 0.75,
        marginRight: 0.75,
        marginTop: 1,
      },
    },
    {
      icon: <WideMarginsIcon classes={{ root: classes.menuItemIcon }} />,
      name: 'Wide',
      pageDimensions: {
        marginBottom: 1,
        marginLeft: 2,
        marginRight: 2,
        marginTop: 1,
      },
    },
  ];

  const isPortrait = pageDimensions.height > pageDimensions.width;
  const isLandscape = pageDimensions.width > pageDimensions.height;

  function parseIndentationValue(value: string): number | null {
    const matches = value.replace(/\s/g, '').match(/^(-?\d+(\.\d+)?)"?$/);
    if (!matches) return null;

    return parseFloat(matches[1]);
  }

  function parseSpacingValue(value: string): number | null {
    const matches = value.replace(/\s/g, '').match(/^(\d+(\.\d+)?)(pt)?$/);

    if (!matches) return null;

    return parseFloat(matches[1]);
  }

  return (
    <ButtonsBarWithOverflowMenu>
      <ButtonMenu
        disabled={editorLoading}
        icon={<PageMarginsIcon />}
        open={pageMarginsOpen}
        tooltip="Page margins"
        onClose={(): void => setPageMarginsOpen(false)}
        onOpen={(): void => setPageMarginsOpen(true)}
      >
        {margins.map(ms => {
          const pd = ms.pageDimensions;
          return (
            <MenuItem
              key={ms.name}
              classes={{ root: classes.menuItem }}
              selected={equals(
                pd,
                pick(
                  [
                    'marginBottom',
                    'marginLeft',
                    'marginRight',
                    'marginTop',
                  ] satisfies (keyof PageDimensions)[],
                  pageDimensions
                )
              )}
              onClick={(): void => {
                if (!currentUserId || !editingMode) return;

                AuthorizedTransforms.updatePageDimensions(editor, {
                  authorId: currentUserId,
                  editingMode,
                  newPageDimensions: pd,
                });
                setPageMarginsOpen(false);
                ClientTransforms.focus(editor);
              }}
            >
              {ms.icon}
              <div>
                <div className={classes.menuItemHeader}>{ms.name}</div>
                <div className={classes.menuItemDescription}>
                  Top: {pd.marginTop}”, Bottom: {pd.marginBottom}”, Left: {pd.marginLeft}”, Right:{' '}
                  {pd.marginRight}”
                </div>
              </div>
            </MenuItem>
          );
        })}
        <Divider className={classes.buttonMenuDivider} orientation="horizontal" />
        <MenuItem
          classes={{ root: classes.menuItem }}
          onClick={(): void => {
            setPageSetupDialogOpen(true);
            setPageMarginsOpen(false);
          }}
        >
          Custom margins...
        </MenuItem>
      </ButtonMenu>
      <ButtonMenu
        disabled={editorLoading}
        icon={<PageOrientationIcon />}
        open={pageOrientationOpen}
        tooltip="Page orientation"
        onClose={(): void => setPageOrientationOpen(false)}
        onOpen={(): void => setPageOrientationOpen(true)}
      >
        <MenuItem
          classes={{ root: classes.menuItem, selected: classes.menuItemSelected }}
          selected={isPortrait}
          onClick={(): void => {
            if (!currentUserId || !editingMode) return;

            AuthorizedTransforms.updateOrientation(editor, {
              authorId: currentUserId,
              editingMode,
              orientation: 'portrait',
            });

            setPageOrientationOpen(false);

            ClientTransforms.focus(editor);
          }}
        >
          <PortraitIcon classes={{ root: classes.menuItemIcon }} />
          <div>Portrait</div>
        </MenuItem>
        <MenuItem
          classes={{ root: classes.menuItem, selected: classes.menuItemSelected }}
          selected={isLandscape}
          onClick={(): void => {
            if (!currentUserId || !editingMode) return;

            AuthorizedTransforms.updateOrientation(editor, {
              authorId: currentUserId,
              editingMode,
              orientation: 'landscape',
            });

            setPageOrientationOpen(false);

            ClientTransforms.focus(editor);
          }}
        >
          <LandscapeIcon classes={{ root: classes.menuItemIcon }} />
          <div>Landscape</div>
        </MenuItem>
      </ButtonMenu>
      <ButtonMenu
        disabled={editorLoading}
        icon={<PageSizeIcon />}
        open={pageSizeOpen}
        tooltip="Page size"
        onClose={(): void => setPageSizeOpen(false)}
        onOpen={(): void => setPageSizeOpen(true)}
      >
        {pageSizes
          .filter((pageSize: PageSize) => pageSize.shortlist)
          .map((pageSize: PageSize) => {
            return (
              <MenuItem
                key={pageSize.name}
                classes={{ root: classes.menuItem }}
                selected={
                  pageDimensions.height === pageSize.height &&
                  pageDimensions.width === pageSize.width
                }
                onClick={(): void => {
                  if (!currentUserId || !editingMode) return;

                  AuthorizedTransforms.updatePageDimensions(editor, {
                    authorId: currentUserId,
                    editingMode: editingMode,
                    newPageDimensions: getPageSizeByName(pageSize.name, isLandscape),
                  });
                  setPageSizeOpen(false);
                  ClientTransforms.focus(editor);
                }}
              >
                <div>
                  <div className={classes.menuItemHeader}>{pageSize.name}</div>
                  <div className={classes.menuItemDescription}>
                    {pageSize.width}” x {pageSize.height}”
                  </div>
                </div>
              </MenuItem>
            );
          })}
        <Divider className={classes.buttonMenuDivider} orientation="horizontal" />
        <MenuItem
          classes={{ root: classes.menuItem }}
          onClick={(): void => {
            setPageSetupDialogOpen(true);
            setPageSizeOpen(false);
          }}
        >
          Custom page size...
        </MenuItem>
      </ButtonMenu>
      <Divider className={classes.divider} orientation="vertical" />
      <ParagraphPropStepper
        data-cy="left-indent-stepper"
        derivedValue={indentationLeft}
        disabled={editorLoading}
        icon={<IndentLeftIcon />}
        label="Indent Left"
        parseValue={parseIndentationValue}
        stepIncrement={0.1}
        suffix={'"'}
        onChange={(value): Error | void => {
          if (!currentUserId || !editingMode) return;

          return AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
            indentationLeft: value,
          });
        }}
      />
      <ParagraphPropStepper
        data-cy="right-indent-stepper"
        derivedValue={indentationRight}
        disabled={editorLoading}
        icon={<IndentRightIcon />}
        label="Indent Right"
        parseValue={parseIndentationValue}
        stepIncrement={0.1}
        suffix={'"'}
        onChange={(value): Error | void => {
          if (!currentUserId || !editingMode) return;

          return AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
            indentationRight: value,
          });
        }}
      />
      <ParagraphPropStepper
        aria-label="Spacing Before"
        data-cy="spacing-before-stepper"
        derivedValue={spacingBefore}
        disabled={editorLoading}
        icon={<SpacingBeforeIcon />}
        label="Spacing before"
        parseValue={parseSpacingValue}
        stepIncrement={6}
        suffix={' pt'}
        onChange={(value): Error | void => {
          if (!currentUserId || !editingMode) return;

          return AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
            spacingBefore: value,
          });
        }}
      />
      <ParagraphPropStepper
        aria-label="Spacing After"
        data-cy="spacing-after-stepper"
        derivedValue={spacingAfter}
        disabled={editorLoading}
        icon={<SpacingAfterIcon />}
        label="Spacing after"
        parseValue={parseSpacingValue}
        stepIncrement={6}
        suffix={' pt'}
        onChange={(value): Error | void => {
          if (!currentUserId || !editingMode) return;

          return AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, {
            spacingAfter: value,
          });
        }}
      />
    </ButtonsBarWithOverflowMenu>
  );
}
